import { Input } from "@vjal/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRoleDefinition, isCoachingRole, type CoachingRole } from "../domain/roles";
import { useCoachingProfile } from "../hooks/useCoachingQueries";
import { getCoachingOrgId } from "../services/coaching-org-context";
import { switchCoachingOrganization } from "../services/coaching-org-switch";
import type { CoachingMembershipDto } from "../services/coaching-dtos";

type CoachingMembership = CoachingMembershipDto & { coachingRole: CoachingRole };

export function CoachingOrgSwitcher() {
  const profile = useCoachingProfile();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const membershipList = useMemo(
    () =>
      (profile.data?.memberships ?? [])
        .filter((membership): membership is CoachingMembership => isCoachingRole(membership.coachingRole))
        .sort((left, right) => displayOrgName(left).localeCompare(displayOrgName(right), undefined, { sensitivity: "base" })),
    [profile.data?.memberships]
  );
  const activeOrgId = getCoachingOrgId() ?? profile.data?.orgId ?? null;
  const activeMembership = membershipList.find((membership) => membership.orgId === activeOrgId) ?? membershipList[0];
  const filteredMemberships = membershipList.filter((membership) =>
    displayOrgName(membership).toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  if (membershipList.length <= 1) return null;

  function selectMembership(membership: CoachingMembership) {
    setOpen(false);
    void switchCoachingOrganization({
      membership,
      queryClient,
      profile: profile.data,
      navigate,
    });
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "Enter" && filteredMemberships[0]) {
      event.preventDefault();
      selectMembership(filteredMemberships[0]);
    }
  }

  return (
    <div className="relative w-56 sm:w-72" data-testid="coaching-org-switcher">
      <Input
        ref={inputRef}
        aria-label="Switch coaching organisation"
        aria-controls="coaching-org-options"
        aria-expanded={open}
        autoComplete="off"
        className="h-9 border-white/25 bg-white/10 text-sm text-white placeholder:text-white/60 focus-visible:border-white/60 focus-visible:shadow-focus-dark"
        onChange={(event) => {
          setSearch(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={activeMembership ? displayOrgName(activeMembership) : "Choose organisation"}
        role="combobox"
        value={open ? search : activeMembership ? displayOrgName(activeMembership) : ""}
      />
      {open ? (
        <div
          id="coaching-org-options"
          role="listbox"
          aria-label="Coaching organisations"
          className="absolute left-0 right-0 z-50 mt-1 max-h-72 overflow-auto rounded-lg border border-line bg-white py-1 shadow-lg"
        >
          {filteredMemberships.length === 0 ? (
            <p className="m-0 px-3 py-2 text-sm text-ink-muted">No matching organisations.</p>
          ) : (
            filteredMemberships.map((membership) => (
              <button
                key={membership.orgId}
                type="button"
                role="option"
                aria-selected={membership.orgId === activeOrgId}
                className="block min-h-control w-full px-3 py-2 text-left text-sm text-ink-body hover:bg-whisper focus-visible:bg-whisper focus-visible:outline-none"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectMembership(membership)}
              >
                <span className="block font-medium">{displayOrgName(membership)}</span>
                <span className="block text-xs text-ink-muted">{getRoleDefinition(membership.coachingRole).label}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function displayOrgName(membership: CoachingMembershipDto) {
  return membership.orgName?.trim() || `Organisation ${membership.orgId}`;
}
